import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { Grid, Typography, Popover, Box } from '@mui/material';
import moment from 'moment';
import { AppState } from '../../../../store';
import { getDeviceCheckIns, getMessageTransmissions } from '../../../../store/devices/actions';
import useStyles from './styles';
import Clock from '../../../../assets/icons/clock-icon';
import { formatDate } from '../../../../globals/utils';

const Time = ({ suppressPopover }: { suppressPopover?: boolean }) => {
  const dispatch = useDispatch();
  const selectedEntity = useSelector((state: AppState) => state.search.selectedEntity);
  const deviceKey = selectedEntity?.deviceKey;
  const timeFormat = useSelector(
    (state: AppState) => state.userPreference.globalUserPreferences.timeFormat,
  );
  const dateFormat = useSelector(
    (state: AppState) => state.userPreference.globalUserPreferences.dateFormat,
  );
  const timeZone = useSelector(
    (state: AppState) => state.userPreference.customUserPreferences.timeZone,
  );

  const lastCheckIn = useSelector((state: AppState) => state.devices.checkInDetails?.lastCheckIn);

  const messageTransmissions = useSelector(
    (state: AppState) => state.devices.messageTransmissionDetails,
  );
  const [currentTime, setCurrentTime] = useState(moment().utc());
  const [currentTimeInSelectedTZ, setCurrentTimeInSelectedTZ] = useState(moment().tz(timeZone));
  const [anchorElement, setAnchorElement] = useState<any>(null);
  let mounted = true;

  useEffect(() => {
    if (deviceKey && !suppressPopover) {
      dispatch(getDeviceCheckIns(deviceKey));
      dispatch(getMessageTransmissions(deviceKey));
    }
  }, [deviceKey]);
  
  const isValidDate = (date: string) => moment(date).isValid();
  
  useEffect(
    () =>
      (function cleanup() {
        mounted = false;
      }),
  );

  setTimeout(() => {
    if (mounted) {
      setCurrentTime(moment().utc());
      setCurrentTimeInSelectedTZ(moment().tz(timeZone));
    }
  }, 1000);

  const headerFormatString = timeFormat === 'hours12' ? 'h:mm:ss A' : 'HH:mm:ss';
  const classes = useStyles();
  const timeInSelectedTimeZone = currentTimeInSelectedTZ
    .tz(timeZone)
    .format(formatDate(dateFormat, timeFormat));

  const handleClick = (event: any) => {
    if (!suppressPopover) setAnchorElement(event.currentTarget.parentElement);
  };

  return <>
    <Grid
      container
      direction="row"
      justifyContent="space-between"
      id="navbarTime"
      onClick={handleClick}
      className={clsx(classes.grid, { [classes.suppressClickable]: suppressPopover })}
    >
      <Clock className={classes.icon} />
      <Typography variant="body1" className={classes.title}>
        {currentTime.utc().format(`${headerFormatString  } UTC`)}
      </Typography>
    </Grid>
    {anchorElement && (
      <Popover
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        onClose={() => setAnchorElement(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box className={classes.popover} id="LastCheckInPopover">
          <Typography className={classes.popoverHeader}>
            {timeInSelectedTimeZone} ({timeZone})
          </Typography>
          {selectedEntity?.deviceType !== 'PL161' && (<Box className={classes.checkInRow} id="LastCheckInRow">
            <Typography>Last Check In: </Typography>
            {lastCheckIn && (
              <Typography className={classes.checkInTime} id="LastCheckInTime">
                {isValidDate(lastCheckIn) ? `${moment(lastCheckIn).utc().format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
              </Typography>
            )}
            {!lastCheckIn && (
              <Typography className={classes.checkInTime} id="LastCheckInTime">
                N/A
              </Typography>
            )}
          </Box>
          )}
          {selectedEntity?.deviceType !== 'PL444' &&
            selectedEntity?.commercialType !== 'PL141' &&
            selectedEntity?.commercialType !== 'PL131' &&
            messageTransmissions?.lastTransmissionTime?.http && (
              <Box className={classes.checkInRow} id="LastHTTPTransmissionRow">
                <Typography>{selectedEntity?.deviceType === 'PL161' ? "Last HTTP Received:" : "Last HTTP Transmission:" }</Typography>
                <Typography className={classes.checkInTime} id="LastHTTPTransmission">
                {isValidDate(messageTransmissions?.lastTransmissionTime.http) ? `${moment(messageTransmissions?.lastTransmissionTime?.http)
                  .utc()
                  .format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
                </Typography>
              </Box>
            )}
          {selectedEntity?.deviceType === 'PL444' &&
            messageTransmissions?.lastTransmissionTime?.http && (
              <Box className={classes.checkInRow} id="LastHTTPTransmissionRow">
                <Typography>Last HTTP Transmission: </Typography>
                <Typography className={classes.checkInTime} id="LastHTTPTransmission">
                {messageTransmissions?.lastTransmissionTime?.http && isValidDate(messageTransmissions?.lastTransmissionTime.http) ?
                  `${moment(messageTransmissions?.lastTransmissionTime?.http)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
                </Typography>
              </Box>
            )}
          {selectedEntity?.deviceType !== 'PL444' &&
            selectedEntity?.commercialType !== 'PL141' &&
            selectedEntity?.commercialType !== 'PL131' &&
            messageTransmissions?.lastTransmissionTime?.saber && (
              <Box className={classes.checkInRow} id="LastSABERTransmissionRow">
                <Typography>{selectedEntity?.deviceType === 'PL161' ? "Last SABER Received:" : "Last SABER Transmission:" } </Typography>
                <Typography className={classes.checkInTime} id="LastSABERTransmission">
                  {isValidDate(messageTransmissions?.lastTransmissionTime.saber) ?`${moment(messageTransmissions?.lastTransmissionTime?.saber)
                    .utc()
                    .format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
                </Typography>
              </Box>
            )}
          {selectedEntity?.deviceType && selectedEntity?.deviceType !== 'PL240B' &&
            messageTransmissions?.lastTransmissionTime?.mqtt && (
              <Box className={classes.checkInRow} id="LastMQTTTransmissionRow">
                <Typography>Last MQTT Transmission: </Typography>
                <Typography className={classes.checkInTime} id="LastMQTTTransmission">
                {isValidDate(messageTransmissions?.lastTransmissionTime.mqtt) ? `${moment(messageTransmissions?.lastTransmissionTime?.mqtt)
                  .utc()
                  .format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
                </Typography>
              </Box>
            )}
            {selectedEntity?.deviceType !== 'PL444' &&
            selectedEntity?.commercialType !== 'PL141' &&
            selectedEntity?.commercialType !== 'PL131' &&
            messageTransmissions?.lastTransmissionTime?.sbd_saber && (
              <Box className={classes.checkInRow} id="LastSBDTransmissionRow">
                <Typography>Last SBD Transmission: </Typography>
                <Typography className={classes.checkInTime} id="LastSBDTransmission">
                {isValidDate(messageTransmissions?.lastTransmissionTime.sbd_saber) ? `${moment(messageTransmissions?.lastTransmissionTime?.sbd_saber)
                  .utc()
                  .format(formatDate(dateFormat, timeFormat))} UTC` : `N/A`}
                </Typography>
              </Box>
            )}
        </Box>
      </Popover>
    )}
  </>;
};

export { Time };
